/**
 * React Query Context Configuration
 * 
 * Creates and configures the QueryClient for TanStack Query
 */

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from './types';

let _queryClient: QueryClient | null = null;

/**
 * Global error handler for React Query
 * Handles both query and mutation errors
 */
const AUTH_REDIRECT_MESSAGES = new Set(['Unauthenticated', 'Unauthorized', 'Salesforce session expired']);

const onError = (error: unknown, query: { meta?: Record<string, unknown> }): void => {
  if (query.meta?.silent) return;
  console.error('Fetching Error', error);

  // Extract error message from axios error response
  const axiosError = error as AxiosError<ErrorResponse>;

  let errorMessage = 'An unexpected error occurred';

  if (axiosError.response?.data) {
    // Backend format: { message, status, statusCode }
    errorMessage =
      axiosError.response.data.message ||
      axiosError.response.data.error ||
      errorMessage;
  } else if (axiosError.message) {
    errorMessage = axiosError.message;
  }

  // Auth redirect errors already show their own toast via clearSessionAndRedirectToLogin
  if (AUTH_REDIRECT_MESSAGES.has(errorMessage)) return;

  // Show error toast
  toast.error(errorMessage, {
    duration: 4000,
    position: 'top-right',
    icon: '❌',
  });
};

/**
 * Create and configure QueryClient instance
 */
export function getQueryClient(): QueryClient {
  if (_queryClient) {
    return _queryClient;
  }
  _queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => onError(error, query),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => onError(error, mutation),
    }),
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: 2,
        staleTime: 60 * 1000, // 1 minute
        // Keep data in memory for 30 min even when no component is using it,
        // so navigating away briefly and coming back is instant. Each hook
        // can still set its own staleTime to control re-fetching behavior.
        gcTime: 30 * 60 * 1000,
      },
      mutations: {
        retry: 1,
      },
    },
  });

  return _queryClient;
}
